import { TZDate } from '@date-fns/tz';
import { AppError } from './errors';

/**
 * Czas: użytkownik wpisuje datę i godziny w Europe/Warsaw, w bazie przechowujemy
 * `started_at`/`ended_at` (timestamptz, UTC) i `duration_seconds` (integer).
 * Daty biznesowe (`date`) to stringi "YYYY-MM-DD" interpretowane w Europe/Warsaw.
 */
export const APP_TIME_ZONE = 'Europe/Warsaw';

/** "YYYY-MM-DD" */
export type LocalDate = string;
/** "HH:MM" (00:00–23:59) */
export type LocalTime = string;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isLocalDate(value: unknown): value is LocalDate {
  if (typeof value !== 'string') return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}

export function isLocalTime(value: unknown): value is LocalTime {
  return typeof value === 'string' && TIME_RE.test(value);
}

function parseDate(date: LocalDate): [number, number, number] {
  if (!isLocalDate(date)) throw new AppError('VALIDATION_ERROR', `Nieprawidłowa data: ${date}`);
  const m = DATE_RE.exec(date)!;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function parseTime(time: LocalTime): [number, number] {
  if (!isLocalTime(time)) throw new AppError('VALIDATION_ERROR', `Nieprawidłowa godzina: ${time}`);
  const m = TIME_RE.exec(time)!;
  return [Number(m[1]), Number(m[2])];
}

/** Data i godzina lokalna (Europe/Warsaw) → chwila w UTC (Date). Uwzględnia zmianę czasu (DST). */
export function localToUtc(date: LocalDate, time: LocalTime = '00:00'): Date {
  const [y, mo, d] = parseDate(date);
  const [h, mi] = parseTime(time);
  const local = new TZDate(y, mo - 1, d, h, mi, 0, 0, APP_TIME_ZONE);
  return new Date(local.getTime());
}

/** Początek doby lokalnej (00:00 Europe/Warsaw) jako chwila UTC. */
export function startOfLocalDayUtc(date: LocalDate): Date {
  return localToUtc(date, '00:00');
}

/** Chwila → data lokalna "YYYY-MM-DD" w Europe/Warsaw. */
export function toLocalDate(instant: Date): LocalDate {
  const z = new TZDate(instant.getTime(), APP_TIME_ZONE);
  return `${z.getFullYear()}-${pad(z.getMonth() + 1)}-${pad(z.getDate())}`;
}

/** Chwila → godzina lokalna "HH:MM" w Europe/Warsaw. */
export function toLocalTime(instant: Date): LocalTime {
  const z = new TZDate(instant.getTime(), APP_TIME_ZONE);
  return `${pad(z.getHours())}:${pad(z.getMinutes())}`;
}

/** Dzisiejsza data w Europe/Warsaw. */
export function todayLocal(now: Date = new Date()): LocalDate {
  return toLocalDate(now);
}

/** Dodaje dni do daty biznesowej (kalendarzowo, bez wpływu DST). */
export function addDaysLocal(date: LocalDate, days: number): LocalDate {
  const [y, mo, d] = parseDate(date);
  const probe = new Date(Date.UTC(y, mo - 1, d + days));
  return `${probe.getUTCFullYear()}-${pad(probe.getUTCMonth() + 1)}-${pad(probe.getUTCDate())}`;
}

/** Różnica w dniach kalendarzowych: b - a. */
export function diffDaysLocal(a: LocalDate, b: LocalDate): number {
  const [ya, ma, da] = parseDate(a);
  const [yb, mb, db] = parseDate(b);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86_400_000);
}

/** Czy przedział godzin „od–do” tego samego dnia przechodzi przez północ (koniec ≤ początek). */
export function crossesMidnight(start: LocalTime, end: LocalTime): boolean {
  const [sh, sm] = parseTime(start);
  const [eh, em] = parseTime(end);
  return eh * 60 + em <= sh * 60 + sm;
}

export function durationSeconds(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / 1000);
}

export interface TimeRangeInput {
  /** data początku, "YYYY-MM-DD" (Europe/Warsaw) */
  date: LocalDate;
  /** godzina początku, "HH:MM" */
  start: LocalTime;
  /** godzina końca, "HH:MM" */
  end: LocalTime;
  /** data końca; wymagana jawnie, gdy wpis przechodzi przez północ */
  endDate?: LocalDate | null;
}

export interface ResolvedTimeRange {
  startedAt: Date;
  endedAt: Date;
  durationSeconds: number;
  crossesMidnight: boolean;
}

/**
 * Wpis czasu „od–do” w Europe/Warsaw → UTC + czas trwania w sekundach.
 * Wpis przez północ wymaga jawnej daty końca (ARCHITECTURE §14), inaczej VALIDATION_ERROR.
 */
export function resolveTimeRange(input: TimeRangeInput): ResolvedTimeRange {
  const endDate = input.endDate ?? input.date;
  if (endDate === input.date && crossesMidnight(input.start, input.end)) {
    throw new AppError('VALIDATION_ERROR', 'Wpis przechodzi przez północ. Podaj datę końca.', {
      details: { issues: [{ path: 'endDate', message: 'Podaj datę końca' }], crossesMidnight: true },
    });
  }
  const startedAt = localToUtc(input.date, input.start);
  const endedAt = localToUtc(endDate, input.end);
  const seconds = durationSeconds(startedAt, endedAt);
  if (seconds <= 0) {
    throw new AppError('VALIDATION_ERROR', 'Koniec musi być po początku.', {
      details: { issues: [{ path: 'end', message: 'Koniec musi być po początku' }] },
    });
  }
  return { startedAt, endedAt, durationSeconds: seconds, crossesMidnight: endDate !== input.date };
}

/** 24 000 s → "6 h 40 min", 3600 → "1 h", 2400 → "40 min", 0 → "0 min". */
export function formatDuration(seconds: number): string {
  const totalMinutes = Math.round(Math.max(0, seconds) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** 28 800 s → "8:00", 24 000 s → "6:40" (godziny mogą przekraczać 24). */
export function formatClock(seconds: number): string {
  const totalMinutes = Math.round(Math.max(0, seconds) / 60);
  return `${Math.floor(totalMinutes / 60)}:${pad(totalMinutes % 60)}`;
}

const WEEKDAYS_SHORT = ['nd', 'pn', 'wt', 'śr', 'czw', 'pt', 'sb'] as const;

/** Krótki dzień po polsku: "nd 4.10". Przyjmuje datę lokalną albo chwilę. */
export function formatDayShort(value: LocalDate | Date): string {
  const date = typeof value === 'string' ? value : toLocalDate(value);
  const [y, mo, d] = parseDate(date);
  const weekday = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
  return `${WEEKDAYS_SHORT[weekday]} ${d}.${pad(mo)}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
